import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import axios from 'axios';

import { WorkingProfileForm } from '@/components/EditProfilePage/schema';
import LayoutStandard from '@/components/LayoutStandard';
import SignUpForm, { SignUpFormValues } from '@/components/SignUpForm';
import { OnboardingSession } from '@/helpers/sessionUtils';
import useAuth from '@/hooks/useAuth';
import IUser from '@/types/IUser';

import {
  buildClaimProfile,
  claimOnboarding,
  uploadPreviewAssets,
} from './onboardingApi';

interface Props {
  onBack: () => void;
  profileFormData: WorkingProfileForm | null;
  previewToken: string | null;
  onFinalizingChange: (isFinalizing: boolean) => void;
}

type Phase =
  | 'form'
  | 'creating-account'
  | 'uploading-assets'
  | 'creating-profile';

const PHASE_LABELS: Record<Phase, string> = {
  form: '',
  'creating-account': 'Creating your account…',
  'uploading-assets': 'Uploading your assets…',
  'creating-profile': 'Setting up your profile…',
};

function OnboardingStepThree({
  onBack,
  profileFormData,
  previewToken,
  onFinalizingChange,
}: Props) {
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const [phase, setPhase] = useState<Phase>('form');
  const [apiError, setApiError] = useState<string | null>(null);
  const [emailFieldError, setEmailFieldError] = useState<string | undefined>();

  const isLoading = phase !== 'form';

  function handleBeforeOAuthRedirect() {
    if (profileFormData) {
      OnboardingSession.save({ profileFormData, previewToken });
    }
  }

  function handleApiError(err: unknown) {
    setPhase('form');
    onFinalizingChange(false);
    if (!axios.isAxiosError(err)) {
      setApiError('Something went wrong. Please try again.');
      return;
    }

    const status = err.response?.status;
    const data = err.response?.data;

    const emailErrors: string[] = data?.errors?.email ?? [];
    if (
      emailErrors.some(
        (msg: string) =>
          msg.toLowerCase().includes('taken') ||
          msg.toLowerCase().includes('exist'),
      )
    ) {
      setEmailFieldError(
        'An account with this email already exists. Please log in instead.',
      );
      return;
    }

    if (status === 409) {
      // Claim conflict — profile already claimed
      setApiError(
        'This profile has already been claimed. Please contact support if you think this is an error.',
      );
      return;
    }

    setApiError(
      data?.message ?? data?.error ?? `Request failed (${status ?? 'unknown'})`,
    );
  }

  async function finalize(user: IUser) {
    if (!profileFormData || !previewToken) {
      navigate('/');
      return;
    }

    const orgID = user.organisation_id;
    if (!orgID) {
      navigate('/');
      return;
    }

    // ── Upload preview assets (best-effort) ──────────────────────────────
    setPhase('uploading-assets');
    const assets = await uploadPreviewAssets(orgID, profileFormData);

    // ── Claim the onboarding profile ─────────────────────────────────────
    setPhase('creating-profile');
    const claimProfile = buildClaimProfile(profileFormData, assets);
    const { profile_hash } = await claimOnboarding(previewToken, claimProfile);

    navigate('/', { state: { postOnboardingProfileHash: profile_hash } });
  }

  async function handleSubmit({
    firstName,
    lastName,
    email,
    orgName,
    password,
    confirmPassword,
  }: SignUpFormValues) {
    setApiError(null);
    setEmailFieldError(undefined);

    try {
      setPhase('creating-account');
      onFinalizingChange(true);
      await signUp({
        email: email.trim().toLowerCase(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        organisationName: orgName.trim(),
        password,
        passwordConfirmation: confirmPassword,
        termsAccepted: true,
        onSignedUp: finalize,
      });
    } catch (err) {
      handleApiError(err);
    }
  }

  const profileSummary = profileFormData?.first_name
    ? {
        name: [profileFormData.first_name, profileFormData.last_name]
          .filter(Boolean)
          .join(' '),
        subtitle: [profileFormData.job_title, profileFormData.company_name]
          .filter(Boolean)
          .join(' · '),
        imageUrl: profileFormData.photo?.small_url,
      }
    : undefined;

  return (
    <LayoutStandard heading="Create your account" backgroundColor="bg-gray-50">
      <SignUpForm
        initialFirstName={profileFormData?.first_name ?? ''}
        initialLastName={profileFormData?.last_name ?? ''}
        initialEmail={profileFormData?.email ?? ''}
        initialOrgName={profileFormData?.company_name ?? ''}
        emailDisabled={!!profileFormData?.email?.trim()}
        loadingLabel={isLoading ? PHASE_LABELS[phase] : undefined}
        apiError={apiError}
        fieldErrors={emailFieldError ? { email: emailFieldError } : undefined}
        onSubmit={handleSubmit}
        onBeforeOAuthRedirect={handleBeforeOAuthRedirect}
        profileSummary={profileSummary}
        onBack={onBack}
        backLabel="← Back to edit profile"
      />
    </LayoutStandard>
  );
}

export default OnboardingStepThree;
